import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Rocket, Shield, FlaskConical, Power } from "lucide-react";
import { toast } from "sonner";

type Persona = {
  id: string;
  name: string;
  rollout_state: string;
  jarvis_capability: boolean;
  jarvis_voice_provider: string | null;
  jarvis_voice_id: string | null;
  version: number | null;
};

type GoldenStat = { total: number; ran: number; passed: number };

const STATE_META: Record<string, { label: string; icon: typeof Rocket; tone: string }> = {
  off: { label: "Desligado", icon: Power, tone: "bg-muted text-muted-foreground" },
  shadow: { label: "Sombra", icon: Shield, tone: "bg-secondary text-secondary-foreground" },
  canary: { label: "Canário", icon: FlaskConical, tone: "bg-amber-500/15 text-amber-700 dark:text-amber-300" },
  on: { label: "Em produção", icon: Rocket, tone: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" },
};

export function JarvisPersonasTab() {
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [golden, setGolden] = useState<Record<string, GoldenStat>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("syra_ai_agents_safe")
      .select("id, name, rollout_state, jarvis_capability, jarvis_voice_provider, jarvis_voice_id, version")
      .eq("jarvis_capability", true)
      .order("name");
    if (error) { toast.error(error.message); setLoading(false); return; }
    const list = (data ?? []) as unknown as Persona[];
    setPersonas(list);

    if (list.length) {
      const ids = list.map(p => p.id);
      const { data: g } = await supabase
        .from("jarvis_persona_golden")
        .select("persona_id, last_run_passed, last_run_at")
        .in("persona_id", ids);
      const stats: Record<string, GoldenStat> = {};
      for (const id of ids) stats[id] = { total: 0, ran: 0, passed: 0 };
      for (const row of (g ?? []) as Array<{ persona_id: string; last_run_passed: boolean | null; last_run_at: string | null }>) {
        const s = stats[row.persona_id]; if (!s) continue;
        s.total += 1;
        if (row.last_run_at) s.ran += 1;
        if (row.last_run_passed === true) s.passed += 1;
      }
      setGolden(stats);
    }
    setLoading(false);
  };

  useEffect(() => { void load(); }, []);

  const publish = async (p: Persona) => {
    setBusy(p.id);
    const { error } = await supabase.rpc("jarvis_persona_publish", { p_persona_id: p.id });
    setBusy(null);
    if (error) { toast.error(`Bloqueado: ${error.message}`); return; }
    toast.success(`${p.name} promovida para produção`);
    void load();
  };

  const setState = async (p: Persona, state: string) => {
    setBusy(p.id);
    const { error } = await supabase.from("syra_ai_agents").update({ rollout_state: state }).eq("id", p.id);
    setBusy(null);
    if (error) { toast.error(error.message); return; }
    toast.success(`Estado: ${state}`);
    void load();
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold font-headline">Personas</h2>
        <p className="text-xs text-muted-foreground">Gerencie versionamento e rollout das personas Jarvis. Publicação requer ≥10 golden cases e ≥85% pass.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {personas.map((p) => {
          const meta = STATE_META[p.rollout_state] ?? STATE_META.off;
          const Icon = meta.icon;
          const stat = golden[p.id] ?? { total: 0, ran: 0, passed: 0 };
          const passRate = stat.ran > 0 ? stat.passed / stat.ran : 0;
          const canPublish = stat.total >= 10 && passRate >= 0.85 && p.rollout_state !== "on";
          return (
            <Card key={p.id} className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{p.name}</div>
                  <div className="text-[11px] text-muted-foreground">v{p.version ?? 1} · {p.jarvis_voice_provider ?? "—"}</div>
                </div>
                <Badge className={meta.tone} variant="secondary">
                  <Icon className="w-3 h-3 mr-1" /> {meta.label}
                </Badge>
              </div>
              <div className="text-xs grid grid-cols-3 gap-2">
                <div className="rounded-md bg-muted/40 p-2">
                  <div className="text-muted-foreground">Golden</div>
                  <div className="font-semibold tabular-nums">{stat.total}</div>
                </div>
                <div className="rounded-md bg-muted/40 p-2">
                  <div className="text-muted-foreground">Executados</div>
                  <div className="font-semibold tabular-nums">{stat.ran}</div>
                </div>
                <div className="rounded-md bg-muted/40 p-2">
                  <div className="text-muted-foreground">Pass</div>
                  <div className="font-semibold tabular-nums">{(passRate * 100).toFixed(0)}%</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" disabled={busy === p.id || p.rollout_state === "shadow"} onClick={() => setState(p, "shadow")}>Sombra</Button>
                <Button size="sm" variant="outline" disabled={busy === p.id || p.rollout_state === "canary"} onClick={() => setState(p, "canary")}>Canário</Button>
                <Button size="sm" disabled={!canPublish || busy === p.id} onClick={() => publish(p)}>
                  <Rocket className="w-3 h-3 mr-1" /> Publicar
                </Button>
                <Button size="sm" variant="ghost" disabled={busy === p.id || p.rollout_state === "off"} onClick={() => setState(p, "off")}>Desligar</Button>
              </div>
            </Card>
          );
        })}
        {personas.length === 0 && (
          <Card className="p-8 text-center text-sm text-muted-foreground md:col-span-2">
            Nenhuma persona com Jarvis habilitado.
          </Card>
        )}
      </div>
    </div>
  );
}
